/**
 * Period helpers for the Monthly Report page — task 156 D2.
 *
 * Translates the URL query string for `/reports/monthly` into a structured
 * shape the page can render, and back. Keeping the logic in one place
 * means the period-selector dropdown, the picker controls, and the data
 * hook all agree on what "period=quarter" with no `quarter=` param means
 * (default to the current quarter, not throw).
 *
 * The shapes accepted here are a strict superset of what the server's
 * `parsePeriod` accepts (see web/src/reports/period.ts). Validation
 * mismatches surface as 400s from the server — this module just builds
 * the URL and falls back to safe defaults.
 */

export type PeriodKind = 'month' | 'week' | 'quarter' | 'custom';

export interface PeriodSelection {
  kind: PeriodKind;
  /** Set when kind === 'month' */
  month?: string;
  /** Set when kind === 'week' */
  week?: string;
  /** Set when kind === 'quarter' */
  quarter?: string;
  /** Set when kind === 'custom' */
  startDate?: string;
  endDate?: string;
}

/** Format a Date as `YYYY-MM-DD` in UTC. */
export function formatYmd(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Format `YYYY-MM` for a Date in UTC. */
export function formatYearMonth(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/** Compute the ISO 8601 week-numbering year + week for a Date. */
export function isoWeekOf(d: Date): { year: number; week: number } {
  // Standard algorithm: shift to the Thursday of the same ISO week, then
  // count weeks since 4 Jan of the resulting year.
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dow = date.getUTCDay() === 0 ? 7 : date.getUTCDay();
  date.setUTCDate(date.getUTCDate() + 4 - dow);
  const year = date.getUTCFullYear();
  const yearStart = new Date(Date.UTC(year, 0, 1));
  const week = Math.ceil(((date.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return { year, week };
}

/** Format an ISO week as `YYYY-W##`. */
export function formatIsoWeek(year: number, week: number): string {
  return `${year}-W${String(week).padStart(2, '0')}`;
}

/** Compute the calendar quarter (1..4) for a Date. */
export function quarterOf(d: Date): { year: number; quarter: number } {
  return { year: d.getUTCFullYear(), quarter: Math.floor(d.getUTCMonth() / 3) + 1 };
}

/**
 * For the period selector's `Custom` default — return `[YYYY-MM-01,
 * YYYY-MM-LAST]` where the month is `now`'s UTC month. Centralised here so
 * the page doesn't need to call `new Date(...)` (which the eslint config
 * forbids outside this module and a handful of others).
 */
export function thisMonthDateRange(now: Date): { startDate: string; endDate: string } {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const start = new Date(Date.UTC(y, m, 1));
  // Day 0 of the next month is the last day of this month.
  const end = new Date(Date.UTC(y, m + 1, 0));
  return { startDate: formatYmd(start), endDate: formatYmd(end) };
}

/**
 * Parse the URL search params into a PeriodSelection. Falls back to the
 * current month when no period-related parameters are present (the
 * "default behaviour" guarantee from D2).
 */
export function parsePeriodFromSearch(searchParams: URLSearchParams, now: Date): PeriodSelection {
  const period = searchParams.get('period');
  const month = searchParams.get('month');
  const week = searchParams.get('week');
  const quarter = searchParams.get('quarter');
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');

  // Bare `month=YYYY-MM` (no `period`) → month form.
  if (!period && month) {
    return { kind: 'month', month };
  }

  switch (period) {
    case 'week':
      return {
        kind: 'week',
        week: week ?? formatIsoWeek(isoWeekOf(now).year, isoWeekOf(now).week),
      };
    case 'quarter': {
      const { year, quarter: q } = quarterOf(now);
      return {
        kind: 'quarter',
        quarter: quarter ?? `${year}-Q${q}`,
      };
    }
    case 'custom':
      // Default custom range to "this month" if either endpoint is missing.
      return {
        kind: 'custom',
        startDate: startDate ?? `${formatYearMonth(now)}-01`,
        endDate: endDate ?? formatYmd(now),
      };
    case 'month':
      return { kind: 'month', month: month ?? formatYearMonth(now) };
    default:
      // No `period` and no `month` — default to the current month.
      return { kind: 'month', month: formatYearMonth(now) };
  }
}

/**
 * Render a PeriodSelection as a query string suitable for the
 * `/api/v1/orgs/:orgId/reports/monthly` route AND for the page's URL bar.
 * The two are intentionally identical so the URL can be copy-pasted into
 * a curl command and returns the same window.
 */
export function periodToQueryString(sel: PeriodSelection): string {
  switch (sel.kind) {
    case 'month':
      return sel.month ? `month=${encodeURIComponent(sel.month)}` : '';
    case 'week':
      return sel.week ? `period=week&week=${encodeURIComponent(sel.week)}` : '';
    case 'quarter':
      return sel.quarter ? `period=quarter&quarter=${encodeURIComponent(sel.quarter)}` : '';
    case 'custom':
      if (!sel.startDate || !sel.endDate) return '';
      return `period=custom&startDate=${encodeURIComponent(sel.startDate)}&endDate=${encodeURIComponent(sel.endDate)}`;
  }
}

/**
 * Render a PeriodSelection as a `Record<string, string>` suitable for
 * `setSearchParams`. Keys for non-active forms are omitted so React Router
 * cleans them out of the URL on transition.
 */
export function periodToSearchParams(sel: PeriodSelection): Record<string, string> {
  switch (sel.kind) {
    case 'month':
      return sel.month ? { month: sel.month } : {};
    case 'week':
      return sel.week ? { period: 'week', week: sel.week } : { period: 'week' };
    case 'quarter':
      return sel.quarter ? { period: 'quarter', quarter: sel.quarter } : { period: 'quarter' };
    case 'custom':
      if (!sel.startDate || !sel.endDate) return { period: 'custom' };
      return { period: 'custom', startDate: sel.startDate, endDate: sel.endDate };
  }
}

/**
 * For drill-down navigation — turn a PeriodSelection into the
 * `?startDate=…&endDate=…` query the detail pages already understand.
 * Returns the canonical YYYY-MM-DD bounds of the selected window.
 */
export function periodToDateRangeParams(
  sel: PeriodSelection,
  now: Date,
): { startDate: string; endDate: string } {
  // Resolve the period to a [startDate, endDate] inclusive day pair. The
  // server's parsers (in src/reports/period.ts) produce a half-open
  // [gte, lt) UTC range; we mirror their math here so the date-range the
  // detail pages receive is exactly the window the report covered.
  switch (sel.kind) {
    case 'month': {
      const m = sel.month ?? formatYearMonth(now);
      const [yy, mm] = m.split('-').map(Number);
      const start = new Date(Date.UTC(yy, mm - 1, 1));
      const end = new Date(Date.UTC(yy, mm, 0)); // last day of month
      return { startDate: formatYmd(start), endDate: formatYmd(end) };
    }
    case 'week': {
      const w = sel.week ?? formatIsoWeek(isoWeekOf(now).year, isoWeekOf(now).week);
      const m = /^(\d{4})-W(\d{2})$/.exec(w);
      if (!m) {
        // Fall back to "this week" — defensive only.
        return periodToDateRangeParams(
          { kind: 'week', week: formatIsoWeek(isoWeekOf(now).year, isoWeekOf(now).week) },
          now,
        );
      }
      const year = Number(m[1]);
      const week = Number(m[2]);
      const jan4 = new Date(Date.UTC(year, 0, 4));
      const jan4Dow = jan4.getUTCDay() === 0 ? 7 : jan4.getUTCDay();
      const ordinal = week * 7 - jan4Dow - 2;
      const monday = new Date(Date.UTC(year, 0, ordinal));
      const sunday = new Date(monday.getTime() + 6 * 86_400_000);
      return { startDate: formatYmd(monday), endDate: formatYmd(sunday) };
    }
    case 'quarter': {
      const q = sel.quarter ?? `${quarterOf(now).year}-Q${quarterOf(now).quarter}`;
      const m = /^(\d{4})-Q([1-4])$/.exec(q);
      if (!m) {
        return periodToDateRangeParams(
          {
            kind: 'quarter',
            quarter: `${quarterOf(now).year}-Q${quarterOf(now).quarter}`,
          },
          now,
        );
      }
      const year = Number(m[1]);
      const qIdx = Number(m[2]);
      const startMonth = (qIdx - 1) * 3;
      const start = new Date(Date.UTC(year, startMonth, 1));
      const end = new Date(Date.UTC(year, startMonth + 3, 0)); // last day of the quarter
      return { startDate: formatYmd(start), endDate: formatYmd(end) };
    }
    case 'custom':
      return {
        startDate: sel.startDate ?? `${formatYearMonth(now)}-01`,
        endDate: sel.endDate ?? formatYmd(now),
      };
  }
}

/** Human-friendly label for the page's window indicator. */
export function periodLabel(sel: PeriodSelection): string {
  switch (sel.kind) {
    case 'month':
      return sel.month ?? '';
    case 'week':
      return sel.week ?? '';
    case 'quarter':
      return sel.quarter ?? '';
    case 'custom': {
      if (!sel.startDate || !sel.endDate) return '';
      return `${sel.startDate} – ${sel.endDate}`;
    }
  }
}
